import React from 'react';
import { ColumnLayout, Container, Header, Link as PolarisLink, Spinner } from '@amzn/awsui-components-react/polaris';
import { renderElement, renderField, renderLink } from '../../Components/tableUtils';
import CopyToClipboardButton from '../../Components/copyToClipboardButton';
import { createIsengardLink, getDomainFromHost } from '../../../utils';
import { AppConfig } from '../../../api/types';
import useConfig from '../../../hooks/useConfig';
import { Broker, BrokerEngineType, EC2Instance, EC2Volume, Link } from '@amzn/amazonmq-opsconsole-client';
import AmazonMqBrokerAccountIsenLinks from "./AmazonMqBrokerAccountIsenLinks";
import {SpaceBetween} from "@amzn/awsui-components-react";

type Props = {
    broker: Broker,
    brokerInstances: EC2Instance[] | undefined
}

function createEBSDataPartitionLink(brokerInstances: EC2Instance[]): Link | undefined {
    if (brokerInstances === undefined) {
        return undefined;
    }

    if (brokerInstances.length === 0) {
        return undefined;
    }

    let firstInstance = brokerInstances[0];
    let volumes : EC2Volume[] = firstInstance.volumes.filter(v => v.size != 32);
    if (volumes.length === 0) {
        return undefined;
    }
    let firstVolume = volumes[0];
    return {
        name: firstVolume.volumeId + " (" + firstVolume.size + " GB)",
        href: firstVolume.ebsStatsLink
    }
}

function createEBSRootPartitionLink(brokerInstances: EC2Instance[]): Link | undefined {
    if (brokerInstances === undefined) {
        return undefined;
    }

    if (brokerInstances.length === 0) {
        return undefined;
    }

    let firstInstance = brokerInstances[0];
    let volumes : EC2Volume[] = firstInstance.volumes;
    if (volumes.length === 0) {
        return undefined;
    }
    let firstVolume = volumes[0];
    return {
        name: firstVolume.volumeId + " (" + firstVolume.size + " GB)",
        href: firstVolume.ebsStatsLink
    }
}

function renderDDB(broker: Broker, config: AppConfig): React.ReactNode {
    let destination = `dynamodbv2/home?region=${broker.regionName}#edit-item?itemMode=2&pk=${broker.id}&route=ROUTE_ITEM_EXPLORER&sk=&table=Brokers`;
    let readOnlyIsenLink = createIsengardLink(config.isengardEndpoint, broker.brokerInfo.controlPlaneAccountId, 'ReadOnly', destination);
    let amazonMqOpsIsenLink =  createIsengardLink(config.isengardEndpoint, broker.brokerInfo.controlPlaneAccountId, 'AmazonMqOpsZeroCustomerDataHandling', destination);
    return (
        <div style={{display: "flex", gap: 8, alignItems: 'center', alignContent: 'center'}}>
            <div>Brokers Entry</div>
            <div style={{display: "flex", gap: 2, alignItems: 'center'}}><PolarisLink href={readOnlyIsenLink} target='_blank'>ReadOnly</PolarisLink><CopyToClipboardButton text={readOnlyIsenLink} textDescription='ReadOnly link'/></div>
            <div style={{display: "flex", gap: 2, alignItems: 'center'}}><PolarisLink href={amazonMqOpsIsenLink} target='_blank'>AmazonMqOpsZeroCustomerDataHandling</PolarisLink><CopyToClipboardButton text={amazonMqOpsIsenLink} textDescription='AmazonMqOpsZeroCustomerDataHandling link'/></div>
        </div>
    )
}

function renderControlPlaneAccountIdLinks(broker: Broker, config: AppConfig): React.ReactNode {
    let readOnlyIsenLink = createIsengardLink(config.isengardEndpoint, broker.brokerInfo.controlPlaneAccountId, 'ReadOnly', '');
    let amazonMqSupportOpsReadOnlyIsenLink =  createIsengardLink(config.isengardEndpoint, broker.brokerInfo.controlPlaneAccountId, 'AmazonMqSupportOpsReadOnly', '');
    return (
        <AmazonMqBrokerAccountIsenLinks
            account={broker.brokerInfo.controlPlaneAccountId}
            readOnlyLink={readOnlyIsenLink}
            supportOpsReadOnlyLink={amazonMqSupportOpsReadOnlyIsenLink}
        />
    )
}

function renderCloudwatchLogsInsightsLink(broker: Broker, config: AppConfig): React.ReactNode {
    const linkFragment = `cloudwatch/home?region=${broker.regionName}#logsV2:logs-insights`
    const readOnlyIsenLink = createIsengardLink(config.isengardEndpoint, broker.brokerInfo.cellAccountId, 'ReadOnly', linkFragment);
    const amazonMqSupportOpsReadOnlyIsenLink = createIsengardLink(config.isengardEndpoint, broker.brokerInfo.cellAccountId, 'AmazonMqSupportOpsReadOnly', linkFragment);
    return (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <div>Brokers Entry</div>
            <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                <PolarisLink href={readOnlyIsenLink} target='_blank'>ReadOnly</PolarisLink>
                <CopyToClipboardButton text={readOnlyIsenLink} textDescription='ReadOnly link'/>
            </SpaceBetween>
            <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                <PolarisLink href={amazonMqSupportOpsReadOnlyIsenLink} target='_blank'>SupportOps</PolarisLink>
                <CopyToClipboardButton text={amazonMqSupportOpsReadOnlyIsenLink} textDescription='SupportOps link'/>
            </SpaceBetween>
        </SpaceBetween>
    )
}

function renderBrokerOperationalMetricsCloudWatchInternalLink(broker: Broker, config: AppConfig): React.ReactNode {
    const domain: string = getDomainFromHost();
    const dashbaordName: string = `broker-operational-metrics-${domain.startsWith("dev-") ? "alpha" : domain}-${broker.region}-${broker.brokerInfo.cellId}`;
    const linkFragment = `cloudwatch/home?region=${broker.regionName}#dashboards/dashboard/${dashbaordName}`
    const readOnlyIsenLink = createIsengardLink(config.isengardEndpoint, broker.brokerInfo.cellAccountId, 'ReadOnly', linkFragment);
    const wikiLink: string = `https://cw-dashboards.aka.amazon.com/cloudwatch/dashboardInternal?accountId=${broker.brokerInfo.cellAccountId}&awsPartition=${broker.brokerInfo.arn.split(":")[1]}&name=${dashbaordName}`;
    return (
        <div style={{display: "flex", gap: 8, alignItems: 'center', alignContent: 'center'}}>
            <div style={{display: "flex", gap: 2, alignItems: 'center'}}>
                <PolarisLink href={wikiLink} target='_blank'>CloudWatch Internal (wiki)</PolarisLink>
                <CopyToClipboardButton text={wikiLink} textDescription='Dashboard link'/>
            </div>
            <div style={{display: "flex", gap: 2, alignItems: 'center'}}>
                <PolarisLink href={readOnlyIsenLink} target='_blank'>AWS Console ReadOnly</PolarisLink>
                <CopyToClipboardButton text={readOnlyIsenLink} textDescription='ReadOnly link'/>
            </div>
        </div>

    )
}

const BrokerInfoTable : React.FC<Props> = ({broker, brokerInstances}) => {

    const appConfig = useConfig();

    if (appConfig == undefined) {
        return <></>
    }

    const brokerInfo = broker.brokerInfo;

    let content : React.ReactElement[] = [];
    content.push(renderField("ARN", brokerInfo.arn));
    content.push(renderField("Creation Date", brokerInfo.creationDate));
    content.push(renderField("Publicly Accessible", brokerInfo.publiclyAccessible ? "true" : "false"));
    if (brokerInfo.codeDeployApplicationName && brokerInfo.codeDeployDeploymentGroupName) {
        content.push(renderField("CodeDeploy", `${brokerInfo.codeDeployApplicationName} / ${brokerInfo.codeDeployDeploymentGroupName}`));
    } else {
        content.push(renderField("CodeDeploy", "none"));
    }
    content.push(renderLink("CodeDeploy Logs", brokerInfo.codeDeployLogsLink.name, brokerInfo.codeDeployLogsLink.href));
    if (brokerInfo.cfnStackLink !== undefined && brokerInfo.cfnStackLink !== null) {
        content.push(renderLink("CFN Stack", brokerInfo.cfnStackLink.name, brokerInfo.cfnStackLink.href, brokerInfo.cfnStackLink.name));
    } else {
        content.push(renderField("CFN Stack", "none"));
    }
    if (brokerInfo.efsInfo) {
        content.push(renderField("EFS data partition", brokerInfo.efsInfo));
    }

    if (brokerInstances === undefined) {
        content.push(renderElement("EBS data partition", <Spinner />, ""));
        content.push(renderElement("EBS root partition", <Spinner />, ""));
    } else {
        let ebsDataPartitionLink: Link | undefined = createEBSDataPartitionLink(brokerInstances);

        if (ebsDataPartitionLink !== undefined) {
            content.push(renderLink("EBS data partition", ebsDataPartitionLink.name, ebsDataPartitionLink.href, ebsDataPartitionLink.href));
        } else {
            content.push(renderField("EBS data partition", "None"));
        }
        let ebsRootPartitionLink: Link | undefined = createEBSRootPartitionLink(brokerInstances);

        if (ebsRootPartitionLink !== undefined) {
            content.push(renderLink("EBS root partition", ebsRootPartitionLink.name, ebsRootPartitionLink.href, ebsRootPartitionLink.href));
        } else {
            content.push(renderField("EBS root partition", "None"));
        }
    }
    
    content.push(renderField("EncryptionOptions", brokerInfo.encryptionOptions));
    content.push(renderField("Cell Account ID", brokerInfo.cellAccountId));
    content.push(renderField("Authentication Strategy", brokerInfo.authenticationStrategy));
    
    if (brokerInfo.authBackends) {
        content.push(renderElement("Auth Backends", 
            <div>{brokerInfo.authBackends.split('\n').map((line: string, index: number) => 
                line.trim() && <div key={index}>{line.trim()}</div>
            )}</div>, 
            brokerInfo.authBackends));
    }
    
    content.push(renderElement("Control Plane Account ID", renderControlPlaneAccountIdLinks(broker, appConfig), broker.brokerInfo.controlPlaneAccountId));

    content.push(renderElement("DDB", renderDDB(broker, appConfig), ""));
    content.push(renderElement("Cloudwatch Logs Insights", renderCloudwatchLogsInsightsLink(broker, appConfig), ""));

    if (broker.summary.brokerEngineType === BrokerEngineType.RABBITMQ) {
        content.push(renderElement("Broker Operational dashboard", renderBrokerOperationalMetricsCloudWatchInternalLink(broker, appConfig), ""));
    }

    return (
        <Container header={
            <Header variant="h2">
                Broker Info
            </Header>
        }>
            <ColumnLayout columns={3} variant="text-grid">
                {content}
            </ColumnLayout>
        </Container>
    )
}

export default BrokerInfoTable;